/**

 * AGC: P2P Battle Component (Player vs Player)

 * AGC: Uses WebSocket for real-time matchmaking and battle coordination

 * AGC: Simplified MVP - Basic matchmaking and payment flow

 */



import React, { useState, useEffect, useRef, useCallback } from 'react'

import { useWebSocket } from '../hooks/useWebSocket'

import { useAccount } from 'wagmi'  // AGC: CRITICAL FIX - Get connector for wallet validation

import { useFeatureFlags } from '../hooks/useFeatureFlags'  // AGC: CRITICAL FIX - Detect Farcaster context

import { ethers } from 'ethers'

import { getSafeSigner } from '../utils/getBrowserProvider'

import { InteractiveP2PBattle } from './InteractiveP2PBattle'

import { BuffSelector } from './BuffSelector' // AGC: Import BuffSelector for NFT selection (handles both buffs and potions)

import { MatchmakingLobby } from './MatchmakingLobby' // AGC: Import MatchmakingLobby for click-to-join

import { usePlayerHistory } from '../hooks/usePlayerHistory' // AGC: Import for P2P battle history

import { usePlayerProgression } from '../hooks/usePlayerProgression' // AGC: Import for player level

import { BattleResultModal } from './modals/BattleResultModal' // AGC: Import auto-closing modal
import { useModal } from '../hooks/useModal'

import type { ActivePage } from './MainMenu'

import type { BottomNavKey } from './BottomNav'
import { usePlayerStatus } from '../hooks/useOGBToken' // AGC: Use same hook as TopNav for balance
import { DifficultySelector, Difficulty } from './DifficultySelector' // AGC: Reusable difficulty selector
import type { BattleRecord } from '../hooks/usePlayerHistory' // AGC: Import BattleRecord type

const ENTRY_COST_OGB_WEI = 50n * (10n ** 18n)



interface P2PBattleProps {

  playerAddress: string

  playerLevel: number

  onNavigateToPage?: (page: ActivePage) => void  // AGC: Navigate to other pages

  onNavigateToMarketplace?: () => void  // AGC: Navigate to marketplace

  onNavClick?: (nav: BottomNavKey) => void

  activeNav?: BottomNavKey

  onOpenSuggestion?: () => void

  onOpenSettings?: () => void

  onOpenInventory?: () => void

  autoStartSearch?: boolean  // AGC: Auto-start matchmaking when component mounts

}



export const P2PBattle: React.FC<P2PBattleProps> = ({

  playerAddress,

  playerLevel,

  onNavigateToPage,

  onNavigateToMarketplace,  // AGC: Marketplace navigation

  onNavClick: _onNavClick,

  activeNav: _activeNav,

  onOpenSuggestion: _onOpenSuggestion,

  onOpenSettings: _onOpenSettings,

  onOpenInventory: _onOpenInventory,

  autoStartSearch = false  // AGC: Auto-start matchmaking when component mounts

}) => {

  const [matchSettings, setMatchSettings] = useState<Difficulty>('normal')

  const [matchData, setMatchData] = useState<any>(null)

  const [battleId, setBattleId] = useState<string>('')

  const [isPaying, setIsPaying] = useState(false)

  const [hasPaid, setHasPaid] = useState(false)

  const [isRefunding, setIsRefunding] = useState(false)

  

  // AGC: Import hooks for battle history tracking

  const { addBattleRecord } = usePlayerHistory(playerAddress)

  const { playerStats, calculateBattleXP } = usePlayerProgression(playerAddress)

  const [battleCreatedAt, setBattleCreatedAt] = useState<number>(0)

  const [canRefund, setCanRefund] = useState(false)

  const [timeRemaining, setTimeRemaining] = useState<number>(180) // AGC: Countdown state for UI

  const [battleStarted, setBattleStarted] = useState(false)

  const [opponentData, setOpponentData] = useState<{ address: string, level: number } | null>(null)

  const [selectedBuffs, setSelectedBuffs] = useState<number[]>([]) // AGC: Store selected NFT buffs
  const [selectedPotion, setSelectedPotion] = useState<number | null>(null) // AGC: Store selected potion token ID
  const [isTournamentMatch, setIsTournamentMatch] = useState(false) // AGC: Track if this is a tournament match

  const [statusBanner, setStatusBanner] = useState<{ type: 'error' | 'success'; message: string } | null>(null)

  

  // AGC: Battle result modal state

  const [showResultModal, setShowResultModal] = useState(false)

  const [battleResult, setBattleResult] = useState<{ won: boolean, isDraw: boolean }>({ won: false, isDraw: false })

  

  const { address } = useAccount() // AGC: Get address for usePlayerStatus
  const { balanceWei } = usePlayerStatus(address) // AGC: Use same hook as TopNav

  // AGC: Scroll to top when component mounts to prevent scroll position issues
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])
  
  // AGC: SAFETY - Check for tournament match data from TournamentLobby (with expiration)
  useEffect(() => {
    const tournamentMatchStr = sessionStorage.getItem('tournamentMatch')
    if (tournamentMatchStr) {
      try {
        const tournamentMatch = JSON.parse(tournamentMatchStr)
        
        // AGC: SAFETY - Validate timestamp to prevent stale data from breaking PvP
        const age = Date.now() - (tournamentMatch.timestamp || 0)
        const MAX_AGE = 5 * 60 * 1000 // 5 minutes
        
        if (age > MAX_AGE) {
          console.log('🏆 Tournament match data expired (>5 min) - clearing to prevent PvP issues')
          sessionStorage.removeItem('tournamentMatch')
          setIsTournamentMatch(false)
          return
        }
        
        // AGC: Only set tournament flag if data is fresh
        console.log('🏆 Tournament match detected (age: ' + Math.round(age / 1000) + 's)')
        setIsTournamentMatch(true)
        
        // AGC: Load pre-selected buffs from tournament match (can be empty array - NFTs are optional)
        if (tournamentMatch.selectedBuffs !== undefined) {
          if (Array.isArray(tournamentMatch.selectedBuffs)) {
            setSelectedBuffs(tournamentMatch.selectedBuffs)
            console.log('🏆 Using pre-selected buffs:', tournamentMatch.selectedBuffs)
          } else {
            setSelectedBuffs([])
            console.log('🏆 Invalid selectedBuffs format - defaulting to empty array')
          }
        } else {
          // AGC: No buffs selected - that's OK, NFTs are optional
          setSelectedBuffs([])
          console.log('🏆 No buffs pre-selected - NFTs are optional for tournament matches')
        }
      } catch (error) {
        console.error('[P2PBattle] Failed to parse tournament match info:', error)
        sessionStorage.removeItem('tournamentMatch')
        setIsTournamentMatch(false)
      }
    } else {
      // AGC: SAFETY - No tournament data, ensure flag is false for normal PvP
      setIsTournamentMatch(false)
    }
  }, [])
  
  // AGC: SAFETY - Clear tournament flag if this is a normal PvP match
  useEffect(() => {
    if (matchData) {
      // AGC: Check if backend explicitly marked this as NOT a tournament match
      if (matchData.tournamentMatch === false || !matchData.tournamentMatch) {
        console.log('✅ Normal PvP match detected - clearing tournament flag')
        setIsTournamentMatch(false)
        sessionStorage.removeItem('tournamentMatch')
      } else if (matchData.tournamentMatch === true) {
        console.log('🏆 Tournament match confirmed by backend')
        setIsTournamentMatch(true)
      }
    }
  }, [matchData])
  
  // AGC: SAFETY - Cleanup tournament data when component unmounts (only if battle never started)
  useEffect(() => {
    return () => {
      // AGC: Only clear if we're in tournament mode and haven't started battle
      // AGC: But don't clear if we're navigating away during match setup (give it time)
      if (isTournamentMatch && !battleStarted && !matchData) {
        // AGC: Check if tournament data is very old (more than 30 seconds) before clearing
        const tournamentMatchStr = sessionStorage.getItem('tournamentMatch')
        if (tournamentMatchStr) {
          try {
            const tournamentMatch = JSON.parse(tournamentMatchStr)
            const age = Date.now() - (tournamentMatch.timestamp || 0)
            if (age > 30 * 1000) {
              console.log('🏆 Clearing tournament data (component unmount, battle not started, data old)')
        sessionStorage.removeItem('tournamentMatch')
            } else {
              console.log('🏆 Keeping tournament data (component unmount, but data is fresh - might be navigating)')
      }
          } catch (error) {
            // AGC: Invalid data - clear it
            sessionStorage.removeItem('tournamentMatch')
    }
        }
      }
    }
  }, [isTournamentMatch, battleStarted, matchData])
  
  // AGC: SAFETY - Reset tournament flag when battle ends (but give time for match_found to arrive)
  useEffect(() => {
    if (!matchData && !battleStarted && isTournamentMatch) {
      // AGC: Check if tournament data is fresh (less than 10 seconds old)
      const tournamentMatchStr = sessionStorage.getItem('tournamentMatch')
      if (tournamentMatchStr) {
        try {
          const tournamentMatch = JSON.parse(tournamentMatchStr)
          const age = Date.now() - (tournamentMatch.timestamp || 0)
          const MAX_WAIT_TIME = 10 * 1000 // 10 seconds to wait for match_found
          
          if (age < MAX_WAIT_TIME) {
            console.log('🏆 Waiting for match_found event (age: ' + Math.round(age / 1000) + 's)')
            return // AGC: Don't clear yet - match_found might still arrive
          }
        } catch (error) {
          // AGC: Invalid data - clear it
        }
      }
      
      // AGC: Tournament data is stale or missing - safe to clear
      console.log('🏆 Resetting tournament flag (no active match, data stale)')
      setIsTournamentMatch(false)
      sessionStorage.removeItem('tournamentMatch')
    }
  }, [matchData, battleStarted, isTournamentMatch])

  // AGC: Check if player has required OGB balance
  const hasRequiredOgbBalance = (balanceWei ?? 0n) >= ENTRY_COST_OGB_WEI

  const findMatchButtonRef = useRef<HTMLButtonElement | null>(null) // AGC: Scroll target for matchmaking CTA on mobile

  const payButtonRef = useRef<HTMLButtonElement | null>(null) // AGC: Scroll target for payment CTA on mobile

  const [highlightFindMatchButton, setHighlightFindMatchButton] = useState(false) // AGC: Flash ring for the find match button

  const [highlightPayButton, setHighlightPayButton] = useState(false) // AGC: Flash ring for the pay button

  const hasBuffInteractionRef = useRef(false) // AGC: Track if player interacted with buff selector
  const autoSearchStartedRef = useRef(false) // AGC: Track if auto-search has been triggered



  // AGC: Get connector for resolveSigner() - works with any wallet type (Base App, Farcaster, Desktop)

  const { connector } = useAccount()

  const { isFarcasterContext } = useFeatureFlags()
  
  // AGC: Modal hook for Base App compatible alerts
  const { alert, Modals } = useModal()

  const showStatusMessage = useCallback(

    (type: 'error' | 'success', message: string) => {

      setStatusBanner({ type, message })



      // AGC: Use modal for errors (works in Base App, Farcaster, and desktop)
      if (type === 'error') {
        alert(message, { type: 'error' })
      }

    },

    [isFarcasterContext]

  )



  const resolveSigner = useCallback(async () => {

    return getSafeSigner({

      connector,

      isFarcasterContext,

      expectedAddress: playerAddress as `0x${string}`

    })

  }, [connector, isFarcasterContext, playerAddress])



  // AGC: Helper to bring critical buttons into view on mobile and add a pulse

  const focusMobileButton = (buttonRef: React.RefObject<HTMLButtonElement | null>, setHighlight: (value: boolean) => void) => {

    const button = buttonRef.current

    if (!button) return



    if (typeof window !== 'undefined' && window.innerWidth <= 768) {

      button.scrollIntoView({ behavior: 'smooth', block: 'center' })

    }



    try {

      button.focus({ preventScroll: true })

    } catch (error) {

      button.focus()

    }

    setHighlight(true)

  }



  // AGC: Fade out matchmaking highlight automatically

  useEffect(() => {

    if (!highlightFindMatchButton) return

    const timeoutId = setTimeout(() => setHighlightFindMatchButton(false), 3000)

    return () => clearTimeout(timeoutId)

  }, [highlightFindMatchButton])



  // AGC: Fade out payment highlight automatically

  useEffect(() => {

    if (!highlightPayButton) return

    const timeoutId = setTimeout(() => setHighlightPayButton(false), 3000)

    return () => clearTimeout(timeoutId)

  }, [highlightPayButton])



  // AGC: Check if battle can be refunded (after 3 minutes)

  useEffect(() => {

    if (!battleCreatedAt) return

    

    const checkRefundEligibility = setInterval(() => {

      const now = Date.now()

      const elapsed = (now - battleCreatedAt) / 1000 // seconds

      const remaining = Math.max(0, 180 - elapsed)

      

      setTimeRemaining(remaining) // AGC: Update countdown

      

      if (elapsed >= 180) { // 3 minutes = 180 seconds

        setCanRefund(true)

      }

    }, 1000)

    

    return () => clearInterval(checkRefundEligibility)

  }, [battleCreatedAt])



  // AGC: Re-focus the pay button whenever buff selection changes during the match confirmation step

  useEffect(() => {

    if (!matchData || hasPaid) return

    if (!hasBuffInteractionRef.current && selectedBuffs.length === 0) return

    focusMobileButton(payButtonRef, setHighlightPayButton)

  }, [selectedBuffs, matchData, hasPaid])



  useEffect(() => {

    if (!matchData) {

      hasBuffInteractionRef.current = false

    }

  }, [matchData])



  // AGC: WebSocket connection
  const webSocketHook = useWebSocket({

    onMatchFound: (data) => {

      console.log(' Match found!', data)
      console.log('🏆 Tournament match check in onMatchFound:', {
        tournamentMatch: data.tournamentMatch,
        currentSelectedBuffs: selectedBuffs,
        isTournamentMatch
      })

      // AGC: CRITICAL - If this is a tournament match, ensure tournament flag is set
      if (data.tournamentMatch === true) {
        console.log('🏆 Tournament match_found received - setting tournament flag')
        setIsTournamentMatch(true)
        
        // AGC: Update sessionStorage timestamp to keep data fresh
        const tournamentMatchStr = sessionStorage.getItem('tournamentMatch')
        if (tournamentMatchStr) {
          try {
            const tournamentMatch = JSON.parse(tournamentMatchStr)
            tournamentMatch.timestamp = Date.now() // AGC: Refresh timestamp
            sessionStorage.setItem('tournamentMatch', JSON.stringify(tournamentMatch))
          } catch (error) {
            console.warn('🏆 Failed to update tournament match timestamp:', error)
          }
        }
      } else {
        // AGC: P2P RECONNECTION - Persist pending match to sessionStorage for browser refresh recovery
        // AGC: Also used by TopNav to show minimize indicator with countdown
        try {
          sessionStorage.setItem('p2pPendingMatch', JSON.stringify({
            battleId: data.battleId,
            opponentAddress: data.opponentAddress || data.opponent?.address,
            opponentLevel: data.opponentLevel || data.opponent?.level,
            difficulty: data.difficulty || 'normal',
            hasPaid: false, // AGC: Will be updated when player pays
            timestamp: Date.now()
          }))
          console.log('🔄 [P2PBattle] Saved pending match to sessionStorage:', data.battleId?.slice(0, 10))
        } catch (error) {
          console.warn('🔄 [P2PBattle] Failed to save pending match to sessionStorage:', error)
        }
      }

      setMatchData(data)

      setBattleId(data.battleId)

      setBattleCreatedAt(Date.now()) // AGC: Save timestamp for refund timer

      // AGC: SAFETY - For tournament matches, preserve selectedBuffs from sessionStorage
      // AGC: Don't reset selectedBuffs if this is a tournament match with pre-selected buffs
      if (data.tournamentMatch === true && isTournamentMatch && selectedBuffs.length > 0) {
        console.log('🏆 Preserving tournament pre-selected buffs:', selectedBuffs)
        // AGC: Keep selectedBuffs as-is (already loaded from sessionStorage)
      } else {
        hasBuffInteractionRef.current = false
      }

      // AGC: CRITICAL - For tournament matches, skip payment and auto-confirm (entry fee already paid)
      if (data.tournamentMatch === true) {
        console.log('🏆 Tournament match detected - skipping payment (entry fee already paid)')
        
        // AGC: Set opponent data from match_found event (needed for InteractiveP2PBattle)
        if (data.opponentAddress && data.opponentLevel) {
          setOpponentData({ address: data.opponentAddress, level: data.opponentLevel })
          console.log('🏆 Tournament opponent data set:', { address: data.opponentAddress, level: data.opponentLevel })
        } else if (data.opponent) {
          setOpponentData({ address: data.opponent.address, level: data.opponent.level })
          console.log('🏆 Tournament opponent data set from opponent object:', data.opponent)
        }
        
        console.log('🏆 Auto-confirming payment for tournament match...')
        setHasPaid(true) // AGC: Mark as paid to skip payment UI
        // AGC: Auto-confirm payment with tournament flag (backend should handle tournament matches differently)
        confirmPayment(data.battleId, playerAddress, '0x0000000000000000000000000000000000000000000000000000000000000000') // AGC: Dummy txHash for tournament matches
        console.log('🏆 Tournament match payment auto-confirmed - waiting for battle_start...')
      } else {
      focusMobileButton(payButtonRef, setHighlightPayButton)
      }

    },

    onBattleStart: (data) => {

      console.log('🔥 Battle starting!', data)

      // AGC: P2P RECONNECTION - Clear pending match and save in-battle state
      try {
        sessionStorage.removeItem('p2pPendingMatch')
        
        // AGC: Save in-battle state for minimize indicator
        sessionStorage.setItem('p2pActiveBattle', JSON.stringify({
          battleId: data.battleId || battleId,
          opponentAddress: data.opponentAddress,
          opponentLevel: data.opponentLevel,
          selectedBuffs: selectedBuffs, // AGC: Save selected buffs for reconnection
          selectedPotion: selectedPotion, // AGC: Save selected potion for reconnection
          currentTurn: 1,
          turnTimeLeft: 60,
          timestamp: Date.now()
        }))
        console.log('🔄 [P2PBattle] Saved in-battle state to sessionStorage')
      } catch (error) {
        console.warn('🔄 [P2PBattle] Failed to save in-battle state to sessionStorage:', error)
      }

      // AGC: Use opponent data directly from battle_start event (source of truth)
      if (data.opponentAddress && data.opponentLevel) {
        setOpponentData({ address: data.opponentAddress, level: data.opponentLevel })
        // AGC: Ensure battleId is set from event (in case it wasn't set earlier)
        if (data.battleId) {
          setBattleId(data.battleId)
        }
      } else if (matchData?.opponent) {
        // AGC: Fallback to matchData if event data is missing
        setOpponentData({ address: matchData.opponent.address, level: matchData.opponent.level })
      }

      setBattleStarted(true)

    },

    onError: (error) => {

      console.error('[ERROR] P2P Error:', error)

      showStatusMessage('error', `Error: ${error}`)

    }

  })

  // AGC: Destructure with type assertion for joinSpecificMatch
  const {
    isConnected,
    isSearching,
    queueStatus,
    findMatch,
    cancelSearch,
    confirmPayment,
    socket
  } = webSocketHook

  // AGC: Get joinSpecificMatch with type safety (TypeScript doesn't infer it correctly)
  const joinSpecificMatch = (webSocketHook as any).joinSpecificMatch as ((playerAddress: string, targetAddress: string, difficulty: string, level: number) => void) | undefined

  // AGC: Check for private game info from PrivateRoom
  const [privateGameInfo, setPrivateGameInfo] = useState<{ gameNumber: number; password: string; isHost: boolean } | null>(null)
  
  // AGC: Check for join target from Home page MatchmakingLobby
  const [joinTarget, setJoinTarget] = useState<{ targetAddress: string; difficulty: string; level: number } | null>(null)

  useEffect(() => {
    // AGC: Check sessionStorage for private game info
    const privateGameStr = sessionStorage.getItem('privateGame')
    if (privateGameStr) {
      try {
        const privateGame = JSON.parse(privateGameStr)
        setPrivateGameInfo(privateGame)
        console.log('[P2PBattle] Private game detected:', privateGame)
        // AGC: Clear sessionStorage after reading
        sessionStorage.removeItem('privateGame')
      } catch (error) {
        console.error('[P2PBattle] Failed to parse private game info:', error)
      }
    }
    
    // AGC: Check sessionStorage for join target from Home page
    const joinTargetStr = sessionStorage.getItem('p2pJoinTarget')
    if (joinTargetStr) {
      try {
        const joinTargetData = JSON.parse(joinTargetStr)
        // AGC: Check if join target is still valid (within 30 seconds)
        if (Date.now() - joinTargetData.timestamp < 30000) {
          setJoinTarget({
            targetAddress: joinTargetData.targetAddress,
            difficulty: joinTargetData.difficulty,
            level: joinTargetData.level
          })
          console.log('[P2PBattle] Join target detected from Home:', joinTargetData)
        }
        // AGC: Clear sessionStorage after reading
        sessionStorage.removeItem('p2pJoinTarget')
      } catch (error) {
        console.error('[P2PBattle] Failed to parse join target info:', error)
      }
    }
  }, [])

  // AGC: Auto-start matchmaking when coming from Home "Play PvP" button (only once)
  useEffect(() => {
    if (autoStartSearch && !autoSearchStartedRef.current && isConnected && hasRequiredOgbBalance && !isSearching && !matchData) {
      console.log('[AGC] Auto-starting PvP matchmaking from Home button')
      autoSearchStartedRef.current = true
      const currentLevel = playerStats?.level || playerLevel
      findMatch(playerAddress, currentLevel, matchSettings)
    }
  }, [autoStartSearch, isConnected, hasRequiredOgbBalance, isSearching, matchData, playerAddress, playerStats?.level, playerLevel, matchSettings, findMatch])

  // AGC: Auto-join specific match from Home page MatchmakingLobby (only once)
  useEffect(() => {
    if (joinTarget && !autoSearchStartedRef.current && isConnected && hasRequiredOgbBalance && !isSearching && !matchData && !autoStartSearch && !privateGameInfo && joinSpecificMatch) {
      console.log('[AGC] Auto-joining specific match from Home:', joinTarget)
      autoSearchStartedRef.current = true
      const currentLevel = playerStats?.level || playerLevel
      
      // AGC: Set difficulty to match target
      setMatchSettings(joinTarget.difficulty as Difficulty)
      
      // AGC: Trigger join via WebSocket
      try {
        joinSpecificMatch(playerAddress, joinTarget.targetAddress, joinTarget.difficulty, currentLevel)
        showStatusMessage('success', `Joining ${joinTarget.targetAddress.slice(0, 6)}...${joinTarget.targetAddress.slice(-4)}'s match...`)
      } catch (error) {
        console.error('[AGC] Error auto-joining match:', error)
        showStatusMessage('error', 'Failed to join match')
      }
    }
  }, [joinTarget, isConnected, hasRequiredOgbBalance, isSearching, matchData, playerAddress, playerStats?.level, playerLevel, joinSpecificMatch, autoStartSearch, privateGameInfo, showStatusMessage])

  // AGC: Auto-start matchmaking for private game (only once)
  useEffect(() => {
    if (privateGameInfo && !autoSearchStartedRef.current && isConnected && hasRequiredOgbBalance && !isSearching && !matchData && !autoStartSearch && !joinTarget) {
      console.log('[AGC] Auto-starting private game matchmaking:', privateGameInfo)
      autoSearchStartedRef.current = true
      const currentLevel = playerStats?.level || playerLevel
      // AGC: TODO - Update findMatch to support private game parameters
      // For now, start regular matchmaking - backend integration needed
      findMatch(playerAddress, currentLevel, matchSettings)
      // AGC: Show message that private game is being created
      showStatusMessage('success', `Private game #${privateGameInfo.gameNumber} - Waiting for opponent...`)
    }
  }, [privateGameInfo, isConnected, hasRequiredOgbBalance, isSearching, matchData, playerAddress, playerStats?.level, playerLevel, matchSettings, findMatch, autoStartSearch, joinTarget, showStatusMessage])
  
  // AGC: Reset auto-search flag when component unmounts or page changes
  useEffect(() => {
    return () => {
      autoSearchStartedRef.current = false
    }
  }, [])

  // AGC: P2P RECONNECTION SYSTEM - Check for pending/active P2P battles on mount
  // AGC: Similar pattern to TournamentLobby lines 392-398
  const hasCheckedP2PReconnectRef = useRef(false)
  
  // AGC: P2P RECONNECTION - Check sessionStorage for saved pending match or active battle on mount
  useEffect(() => {
    if (isTournamentMatch) return // AGC: Don't check for tournament matches
    
    // AGC: Check for pending match
    try {
      const savedMatch = sessionStorage.getItem('p2pPendingMatch')
      if (savedMatch) {
        const matchData = JSON.parse(savedMatch)
        const age = Date.now() - (matchData.timestamp || 0)
        
        // AGC: Only restore if match is less than 5 minutes old
        if (age < 5 * 60 * 1000) {
          console.log('🔄 [P2PBattle] Found saved pending match in sessionStorage:', matchData.battleId?.slice(0, 10))
          // AGC: Don't auto-restore - let the server confirm via check_p2p_reconnect
        } else {
          // AGC: Stale match - clear it
          sessionStorage.removeItem('p2pPendingMatch')
          console.log('🔄 [P2PBattle] Cleared stale pending match from sessionStorage')
        }
      }
    } catch (error) {
      console.warn('🔄 [P2PBattle] Failed to read pending match from sessionStorage:', error)
    }
    
    // AGC: Check for active battle and restore buffs/potions
    try {
      const savedBattle = sessionStorage.getItem('p2pActiveBattle')
      if (savedBattle) {
        const battleData = JSON.parse(savedBattle)
        const age = Date.now() - (battleData.timestamp || 0)
        
        // AGC: Only restore if battle is less than 10 minutes old
        if (age < 10 * 60 * 1000) {
          console.log('🔄 [P2PBattle] Found active battle in sessionStorage - restoring buffs/potions')
          
          if (battleData.selectedBuffs && Array.isArray(battleData.selectedBuffs)) {
            setSelectedBuffs(battleData.selectedBuffs)
            console.log('🔄 [P2PBattle] Restored buffs:', battleData.selectedBuffs)
          }
          if (battleData.selectedPotion !== undefined && battleData.selectedPotion !== null) {
            setSelectedPotion(battleData.selectedPotion)
            console.log('🔄 [P2PBattle] Restored potion:', battleData.selectedPotion)
          }
        } else {
          // AGC: Stale battle - clear it
          sessionStorage.removeItem('p2pActiveBattle')
          console.log('🔄 [P2PBattle] Cleared stale active battle from sessionStorage')
        }
      }
    } catch (error) {
      console.warn('🔄 [P2PBattle] Failed to read active battle from sessionStorage:', error)
    }
  }, [isTournamentMatch])
  
  useEffect(() => {
    if (!socket || !playerAddress || hasCheckedP2PReconnectRef.current) return
    if (isTournamentMatch) return // AGC: Don't check P2P reconnect for tournament matches
    
    console.log('🔄 [P2PBattle] Checking for P2P reconnection...')
    hasCheckedP2PReconnectRef.current = true
    socket.emit('check_p2p_reconnect', { playerAddress })
  }, [socket, playerAddress, isTournamentMatch])
  
  // AGC: P2P RECONNECTION SYSTEM - Visibility change handler
  // AGC: Re-check for P2P battle when page becomes visible (similar to TournamentLobby lines 452-475)
  const lastVisibilityCheckRef = useRef<number>(0)
  
  useEffect(() => {
    if (!socket || !playerAddress) return
    if (isTournamentMatch) return // AGC: Don't check P2P reconnect for tournament matches
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // AGC: Debounce - don't check more than once per 2 seconds
        const now = Date.now()
        if (now - lastVisibilityCheckRef.current < 2000) {
          console.log('🔄 [P2PBattle] Visibility check debounced')
          return
        }
        lastVisibilityCheckRef.current = now
        
        // AGC: Only re-check if we have a match but haven't started battle yet
        if (matchData && hasPaid && !battleStarted) {
          console.log('🔄 [P2PBattle] Page visible - checking P2P reconnect (waiting for battle)')
          socket.emit('check_p2p_reconnect', { playerAddress })
        } else if (!matchData && !isSearching) {
          // AGC: No match data - check if there's a pending battle to reconnect to
          console.log('🔄 [P2PBattle] Page visible - checking for pending P2P battles')
          socket.emit('check_p2p_reconnect', { playerAddress })
        }
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [socket, playerAddress, matchData, hasPaid, battleStarted, isSearching, isTournamentMatch])
  
  // AGC: P2P RECONNECTION SYSTEM - Handle reconnect events from backend
  useEffect(() => {
    if (!socket) return
    
    // AGC: Handle pending match reconnection (match found but not started)
    const handleP2PMatchReconnect = (data: any) => {
      console.log('🔄 [P2PBattle] p2p_match_reconnect received:', data)
      
      // AGC: Restore match data
      setMatchData({
        battleId: data.battleId,
        opponent: data.opponent || { address: data.opponentAddress, level: data.opponentLevel },
        opponentAddress: data.opponentAddress,
        opponentLevel: data.opponentLevel,
        difficulty: data.difficulty,
        reconnect: true
      })
      setBattleId(data.battleId)
      setBattleCreatedAt(Date.now()) // AGC: Reset timer for reconnected matches
      
      // AGC: Restore payment state
      if (data.hasPaid) {
        setHasPaid(true)
        console.log('🔄 [P2PBattle] Restored payment state: paid')
      }
      
      showStatusMessage('success', '🔄 Reconnected to your pending match!')
    }
    
    // AGC: Handle active battle reconnection (battle already started)
    const handleP2PBattleReconnect = (data: any) => {
      console.log('🔄 [P2PBattle] p2p_battle_reconnect received:', data)
      
      // AGC: Restore buffs and potions from sessionStorage
      try {
        const savedBattle = sessionStorage.getItem('p2pActiveBattle')
        if (savedBattle) {
          const battleData = JSON.parse(savedBattle)
          if (battleData.selectedBuffs && Array.isArray(battleData.selectedBuffs)) {
            setSelectedBuffs(battleData.selectedBuffs)
            console.log('🔄 [P2PBattle] Restored buffs from sessionStorage:', battleData.selectedBuffs)
          }
          if (battleData.selectedPotion !== undefined && battleData.selectedPotion !== null) {
            setSelectedPotion(battleData.selectedPotion)
            console.log('🔄 [P2PBattle] Restored potion from sessionStorage:', battleData.selectedPotion)
          }
          
          // AGC: Update sessionStorage with current battle state from backend
          battleData.currentTurn = data.currentTurn || battleData.currentTurn
          battleData.turnTimeLeft = data.turnTimeLeft || battleData.turnTimeLeft
          sessionStorage.setItem('p2pActiveBattle', JSON.stringify(battleData))
        }
      } catch (error) {
        console.warn('🔄 [P2PBattle] Failed to restore buffs/potions from sessionStorage:', error)
      }
      
      // AGC: Store reconnect state for InteractiveP2PBattle to use
      if (data.currentTurn !== undefined) {
        sessionStorage.setItem('p2pReconnectState', JSON.stringify({
          currentTurn: data.currentTurn,
          yourEnergy: data.yourEnergy,
          opponentEnergy: data.opponentEnergy,
          turnTimeLeft: data.turnTimeLeft,
          turnSubmitted: data.turnSubmitted,
          battleLog: data.battleLog || []
        }))
        console.log(`🔄 [P2PBattle] Stored reconnect state: Turn ${data.currentTurn}, ${data.turnTimeLeft}s left`)
      }
      
      // AGC: Set battle data and transition to battle
      setBattleId(data.battleId)
      setOpponentData({ address: data.opponentAddress, level: data.opponentLevel })
      setHasPaid(true)
      setBattleStarted(true)
      
      showStatusMessage('success', `🔄 Reconnected to Turn ${data.currentTurn || 1}!`)
    }
    
    // AGC: Handle no active P2P battle
    const handleNoActiveP2PBattle = () => {
      console.log('🔄 [P2PBattle] No active P2P battle found')
      // AGC: Clear any stale reconnect state
      hasCheckedP2PReconnectRef.current = true
    }
    
    socket.on('p2p_match_reconnect', handleP2PMatchReconnect)
    socket.on('p2p_battle_reconnect', handleP2PBattleReconnect)
    socket.on('no_active_p2p_battle', handleNoActiveP2PBattle)
    
    return () => {
      socket.off('p2p_match_reconnect', handleP2PMatchReconnect)
      socket.off('p2p_battle_reconnect', handleP2PBattleReconnect)
      socket.off('no_active_p2p_battle', handleNoActiveP2PBattle)
    }
  }, [socket, showStatusMessage])

  const handleMatchSettingSelection = (setting: Difficulty) => {

    setMatchSettings(setting)

    focusMobileButton(findMatchButtonRef, setHighlightFindMatchButton)

  }



  const handleBuffSelection = (combinedItems: number[]) => {
    // AGC: Separate buffs (0-3) from potions (10000+) in combined array
    const buffs: number[] = []
    const potions: number[] = []
    
    for (const item of combinedItems) {
      if (item >= 10000) {
        // AGC: Potion (offset by 10000)
        potions.push(item - 10000)
      } else {
        // AGC: Buff (0-3)
        buffs.push(item)
      }
    }
    
    setSelectedBuffs(buffs)
    
    // AGC: Set potion (take first if multiple selected, since P2P only supports 1 potion)
    if (potions.length > 0) {
      setSelectedPotion(potions[0])
    } else {
      setSelectedPotion(null)
    }

    hasBuffInteractionRef.current = true
  }

  // AGC: Handle joining a specific match from MatchmakingLobby
  const handleJoinSpecificMatch = useCallback((targetAddress: string, difficulty: string, _level: number) => {
    console.log(`🎯 P2PBattle: handleJoinSpecificMatch called`, { targetAddress, difficulty, playerAddress, joinSpecificMatch: !!joinSpecificMatch })
    
    if (!joinSpecificMatch) {
      console.error('❌ P2PBattle: joinSpecificMatch function not available')
      showStatusMessage('error', 'Unable to join match - connection issue')
      return
    }
    
    if (!playerAddress) {
      console.error('❌ P2PBattle: playerAddress not available')
      showStatusMessage('error', 'Unable to join match - wallet not connected')
        return
    }
    
    console.log(`🎯 Attempting to join ${targetAddress}'s ${difficulty} match`)
    
    // AGC: Set difficulty to match target
    setMatchSettings(difficulty as Difficulty)
    
    // AGC: Trigger join via WebSocket
    const currentLevel = playerStats?.level || playerLevel
    try {
      joinSpecificMatch(playerAddress, targetAddress, difficulty, currentLevel)
      // AGC: Show status message
      showStatusMessage('success', `Joining ${targetAddress.slice(0, 6)}...${targetAddress.slice(-4)}'s match...`)
    } catch (error) {
      console.error('❌ P2PBattle: Error calling joinSpecificMatch:', error)
      showStatusMessage('error', `Failed to join match: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [joinSpecificMatch, playerAddress, playerLevel, playerStats?.level, showStatusMessage])

  // AGC: Handle payment for battle

  const handlePayBattle = async () => {

    if (!battleId || !playerAddress) return



    // AGC: resolveSigner() already validates the connected address matches playerAddress
    // AGC: No need for strict connector type check - works with any wallet (Base App, Farcaster, Desktop)

    setStatusBanner(null)

    setIsPaying(true)

    

    try {

      // AGC: Get contract instances

      const p2pManagerAddress = import.meta.env.VITE_P2P_MANAGER_ADDRESS

      const buff1155Address = import.meta.env.VITE_BUFF1155_ADDRESS

      // AGC: Use NEW unified OGB token from environment

      const ogbTokenAddress = import.meta.env.VITE_OGB_TOKEN_ADDRESS

      

      if (!p2pManagerAddress) {

        throw new Error('P2P Manager address not configured')

      }



      // AGC: Connect to wallet

      const signer = await resolveSigner()



      // AGC: STEP 1: Approve P2PManager to spend OGB tokens

      console.log(' Step 1/3: Approving P2PManager to spend 50 OGB...')

      console.log('[WAIT] WAITING FOR WALLET... Check extension icon if popup doesn\'t appear!')

      

      const ogbTokenAbi = [

        'function approve(address spender, uint256 amount) external returns (bool)',

        'function allowance(address owner, address spender) external view returns (uint256)'

      ]

      const ogbToken = new ethers.Contract(ogbTokenAddress, ogbTokenAbi, signer)

      

      // Check current allowance

      const currentAllowance = await ogbToken.allowance(playerAddress, p2pManagerAddress)

      const battleCost = ethers.parseUnits('50', 18)

      

      if (currentAllowance < battleCost) {

        console.log(' Requesting OGB approval...')

        // AGC: SECURITY FIX (2025-11-27) - Approve exact amount instead of unlimited
        // AGC: No unlimited authorizations - only approve what's needed for this battle

        const approveTx = await ogbToken.approve(p2pManagerAddress, battleCost)

        console.log('[WAIT] Waiting for approval to be mined...')

        let receipt
        try {
          receipt = await Promise.race([
            approveTx.wait(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
            )
          ])
        } catch (waitError: any) {
          if (waitError.message?.includes('timeout')) {
            console.warn(`  ⚠️  OGB approval timeout (TX sent: ${approveTx.hash}) - continuing...`)
            // AGC: Create a mock receipt for timeout case
            receipt = { status: 1 }
          } else {
            throw waitError
          }
        }

        console.log(`[OK] OGB Approval mined! TX: ${approveTx.hash}`)

        console.log(` Receipt status: ${receipt.status} (1 = success, 0 = failed)`)

        

        if (receipt.status === 0) {

          throw new Error('OGB approval transaction failed on-chain. Please check your wallet.')

        }

        

        // AGC: Wait longer for MetaMask/Base Sepolia (with retries)

        console.log('[WAIT] Verifying OGB allowance on-chain (MetaMask may take 10-15 seconds)...')

        

        let verified = false

        let newAllowance = BigInt(0)

        

        // AGC: Try up to 5 times with 3-second intervals

        for (let attempt = 1; attempt <= 5; attempt++) {

          console.log(` Verification attempt ${attempt}/5...`)

          await new Promise(resolve => setTimeout(resolve, 3000))

          

          newAllowance = await ogbToken.allowance(playerAddress, p2pManagerAddress)

          console.log(`Current OGB allowance: ${ethers.formatUnits(newAllowance, 18)} OGB`)

          

          if (newAllowance >= battleCost) {

            verified = true

            break

          }

        }

        

        if (!verified) {

          console.warn('[WARN]  Allowance verification timed out, but transaction succeeded.')

          console.warn(`Transaction: https://sepolia.basescan.org/tx/${approveTx.hash}`)

          console.warn('Proceeding anyway - the approval should work.')

          // AGC: Don't throw error, just proceed with warning

        } else {

          console.log('[OK] OGB Allowance verified!')

        }

      } else {

        console.log('[OK] OGB Already approved!')

      }



      // AGC: STEP 2: Approve P2PManager to transfer NFTs (if NFTs selected)

      if (selectedBuffs.length > 0) {

        console.log(` Step 2/3: Approving P2PManager to burn ${selectedBuffs.length} NFT(s)...`)

        console.log('[WAIT] WAITING FOR WALLET... Check extension icon if popup doesn\'t appear!')

        

        const buff1155Abi = [

          'function setApprovalForAll(address operator, bool approved) external',

          'function isApprovedForAll(address account, address operator) external view returns (bool)'

        ]

        const buff1155 = new ethers.Contract(buff1155Address, buff1155Abi, signer)

        

        // AGC: Check if already approved

        const isApproved = await buff1155.isApprovedForAll(playerAddress, p2pManagerAddress)

        

        if (!isApproved) {

          console.log(' Requesting NFT approval...')

          const nftApproveTx = await buff1155.setApprovalForAll(p2pManagerAddress, true)

          console.log('[WAIT] Waiting for NFT approval to be mined...')

          let nftReceipt
          try {
            nftReceipt = await Promise.race([
              nftApproveTx.wait(),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
              )
            ])
          } catch (waitError: any) {
            if (waitError.message?.includes('timeout')) {
              console.warn(`  ⚠️  NFT approval timeout (TX sent: ${nftApproveTx.hash}) - continuing...`)
              // AGC: Create a mock receipt for timeout case
              nftReceipt = { status: 1 }
            } else {
              throw waitError
            }
          }

          console.log(`[OK] NFT Approval mined! TX: ${nftApproveTx.hash}`)

          console.log(` Receipt status: ${nftReceipt.status} (1 = success, 0 = failed)`)

          

          if (nftReceipt.status === 0) {

            throw new Error('NFT approval transaction failed on-chain. Please check your wallet.')

          }

          

          // AGC: Wait longer for MetaMask/Base Sepolia (with retries)

          console.log('[WAIT] Verifying NFT approval on-chain (MetaMask may take 10-15 seconds)...')

          

          let nftVerified = false

          let newApproval = false

          

          // AGC: Try up to 5 times with 3-second intervals

          for (let attempt = 1; attempt <= 5; attempt++) {

            console.log(` NFT verification attempt ${attempt}/5...`)

            await new Promise(resolve => setTimeout(resolve, 3000))

            

            newApproval = await buff1155.isApprovedForAll(playerAddress, p2pManagerAddress)

            console.log(`NFT Approval status: ${newApproval}`)

            

            if (newApproval) {

              nftVerified = true

              break

            }

          }

          

          if (!nftVerified) {

            console.warn('[WARN]  NFT approval verification timed out, but transaction succeeded.')

            console.warn(`Transaction: https://sepolia.basescan.org/tx/${nftApproveTx.hash}`)

            console.warn('Proceeding anyway - the approval should work.')

            // AGC: Don't throw error, just proceed with warning

          } else {

            console.log('[OK] NFT Approval verified!')

          }

        } else {

          console.log('[OK] NFT Already approved!')

        }

      }



      // AGC: STEP 3: Pay battle stake (contract ABI)
      // AGC: SAFETY - Check if this is a tournament match and log selectedBuffs
      console.log('🏆 Tournament match check:', {
        isTournamentMatch,
        tournamentMatchFlag: matchData?.tournamentMatch,
        selectedBuffsCount: selectedBuffs.length,
        selectedBuffs
      })

      // AGC: Support both V1 and V2 contract ABIs
      const p2pManagerAbi = [
        // AGC: V2 functions
        'function payBattleStake(bytes32 battleId, address ogbTokenAddress) external',
        'function payBattleStakeWithNFTs(bytes32 battleId, address ogbTokenAddress, address buff1155Address, address potion1155Address, uint256[] calldata buffIds, uint256[] calldata potionIds) external',
        'function trustedBuff1155Contracts(address) public view returns (bool)',
        'function trustedPotion1155Contracts(address) public view returns (bool)',
        // AGC: FIX 2025-11-28 - Solidity public mapping getters EXCLUDE dynamic arrays!
        // AGC: The V2 struct HAS arrays, but the getter doesn't return them
        // AGC: So we use V2 fields WITHOUT the uint256[] arrays
        'function battles(bytes32) public view returns (bytes32 battleId, address player1, address player2, uint256 stake, address ogbTokenAddress, bool player1Paid, bool player2Paid, bool resolved, address winner, uint256 createdAt, uint256 expiresAt)',
        // AGC: V1 functions (fallback)
        'function payBattleStakeWithNFTs(bytes32 battleId, address buff1155Address, uint256[] calldata nftIds) external'
      ]

      const p2pManager = new ethers.Contract(p2pManagerAddress, p2pManagerAbi, signer)

      // AGC: Check battle state before paying (wait for backend to create on-chain)
      console.log(`   Checking battle state before payment...`)
      let battleExists = false
      let maxAttempts = 10
      let attempt = 0
      
      while (!battleExists && attempt < maxAttempts) {
        try {
          const battleState = await p2pManager.battles(battleId)
          // AGC: Check if battle exists (player1 address should not be zero)
          if (battleState && battleState[1] !== '0x0000000000000000000000000000000000000000') {
            battleExists = true
            // AGC: V2 struct: [battleId, player1, player2, stake, ogbTokenAddress, player1Paid, player2Paid, ...]
            const [, player1OnChain, player2OnChain, , , player1Paid, player2Paid] = battleState
            console.log(`  [OK] Battle exists on-chain:`)
            console.log(`    Player1: ${player1OnChain}`)
            console.log(`    Player2: ${player2OnChain}`)
            console.log(`    Player1 Paid: ${player1Paid}`)
            console.log(`    Player2 Paid: ${player2Paid}`)
            
            // AGC: Check if player already paid
            const isPlayer1 = player1OnChain.toLowerCase() === playerAddress.toLowerCase()
            if ((isPlayer1 && player1Paid) || (!isPlayer1 && player2Paid)) {
              throw new Error(`You have already paid for this battle.`)
            }
            break
          }
        } catch (error: any) {
          // AGC: Battle might not exist yet - wait and retry
          if (error.message?.includes('already paid')) {
            throw error
          }
          attempt++
          if (attempt < maxAttempts) {
            console.log(`  ⏳ Battle not found on-chain yet (attempt ${attempt}/${maxAttempts}), waiting 2 seconds...`)
            await new Promise(resolve => setTimeout(resolve, 2000))
          } else {
            throw new Error(`Battle not found on-chain after ${maxAttempts} attempts. The backend may not have created it yet. Please try again.`)
          }
        }
      }

      // AGC: Determine payment method based on selected buffs
      const hasNFTs = selectedBuffs.length > 0
      const stepCount = hasNFTs ? 3 : 2
      
      console.log(` Step ${stepCount}/${stepCount}: Paying 50 OGB for battle:`, battleId)
      console.log(` Selected buffs: ${selectedBuffs.length} (${hasNFTs ? 'WITH NFTs' : 'WITHOUT NFTs'})`)
      console.log('[WAIT] WAITING FOR WALLET... Check extension icon if popup doesn\'t appear!')

      // AGC: Pay battle stake WITH or WITHOUT NFTs (buffs and/or potions)
      // AGC: V2 supports multiple ERC1155 contracts in a single payment (like PvE)
      let tx
      const classicPotion1155Address = import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS
      const hasPotions = selectedPotion !== null && classicPotion1155Address && classicPotion1155Address !== '0x0000000000000000000000000000000000000000'
      
      if (hasNFTs || hasPotions) {
        // AGC: V2 supports both buffs and potions in a single payment
        console.log(` Using ${selectedBuffs.length} NFT buff(s)${hasPotions ? ' + 1 potion' : ''}`)
        
        // AGC: Verify buff ownership if buffs selected
        if (hasNFTs) {
          if (!buff1155Address) {
            throw new Error('Buff1155 address not configured - required for NFT payment')
          }
          
          const buff1155Abi = ['function balanceOf(address account, uint256 id) external view returns (uint256)']
          const buff1155 = new ethers.Contract(buff1155Address, buff1155Abi, signer)
          
          console.log(`   Verifying buff ownership...`)
          for (const tokenId of selectedBuffs) {
            const balance = await buff1155.balanceOf(playerAddress, tokenId)
            console.log(`    Buff ID ${tokenId}: Balance = ${balance}`)
            if (balance === 0n) {
              throw new Error(`You don't own any buff NFTs of type ${tokenId}. Please refresh and try again.`)
            }
          }
          console.log(`  [OK] Player owns all selected buffs`)
          
          // AGC: Verify contracts are trusted (only if V2 contract - optional check)
          try {
            const isBuffTrusted = await p2pManager.trustedBuff1155Contracts(buff1155Address)
            if (!isBuffTrusted) {
              console.warn(`⚠️ Buff1155 contract is not trusted by P2PManagerV2 - continuing anyway (may be old contract)`)
            } else {
              console.log(`  [OK] Buff1155 contract is trusted`)
            }
          } catch (error) {
            // AGC: Old contract doesn't have trustedBuff1155Contracts - that's OK
            console.log(`  [OK] Using legacy P2P Manager (no trust check available)`)
          }
        }
        
        // AGC: Verify potion ownership if potions selected
        if (hasPotions) {
          const potion1155Abi = [
            'function setApprovalForAll(address operator, bool approved) external',
            'function isApprovedForAll(address account, address operator) external view returns (bool)',
            'function balanceOf(address account, uint256 id) external view returns (uint256)'
          ]
          const potion1155 = new ethers.Contract(classicPotion1155Address, potion1155Abi, signer)
          
          console.log(`   Verifying potion ownership...`)
          const potionBalance = await potion1155.balanceOf(playerAddress, selectedPotion)
          console.log(`    Potion ID ${selectedPotion}: Balance = ${potionBalance}`)
          if (potionBalance === 0n) {
            throw new Error(`You don't own any potion NFTs of type ${selectedPotion}. Please refresh and try again.`)
          }
          console.log(`  [OK] Player owns selected potion`)
          
          // AGC: Verify contract is trusted (only if V2 contract - optional check)
          try {
            const isPotionTrusted = await p2pManager.trustedPotion1155Contracts(classicPotion1155Address)
            if (!isPotionTrusted) {
              console.warn(`⚠️ Potion1155 contract is not trusted by P2PManagerV2 - continuing anyway (may be old contract)`)
            } else {
              console.log(`  [OK] Potion1155 contract is trusted`)
            }
          } catch (error) {
            // AGC: Old contract doesn't have trustedPotion1155Contracts - that's OK
            console.log(`  [OK] Using legacy P2P Manager (no trust check available)`)
          }
          
          // AGC: Approve potion contract if needed
          const isPotionApproved = await potion1155.isApprovedForAll(playerAddress, p2pManagerAddress)
          if (!isPotionApproved) {
            console.log(' Requesting potion approval...')
            const potionApproveTx = await potion1155.setApprovalForAll(p2pManagerAddress, true)
            try {
              await Promise.race([
                potionApproveTx.wait(),
                new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
                )
              ])
              console.log('[OK] Potion approval confirmed!')
            } catch (waitError: any) {
              if (waitError.message?.includes('timeout')) {
                console.warn(`  ⚠️  Potion approval timeout (TX sent: ${potionApproveTx.hash}) - continuing...`)
              } else {
                throw waitError
              }
            }
          }
        }
        
        // AGC: Detect contract version (V1 or V2) by checking if trustedBuff1155Contracts exists
        let isV2Contract = false
        try {
          // AGC: Try to call V2 function - if it exists, we have V2
          const provider = signer.provider
          if (provider) {
            const checkAbi = ['function trustedBuff1155Contracts(address) public view returns (bool)']
            const checkContract = new ethers.Contract(p2pManagerAddress, checkAbi, provider)
            await checkContract.trustedBuff1155Contracts(buff1155Address)
            isV2Contract = true
            console.log(`  [OK] Detected P2PBattleManagerV2`)
          }
        } catch (error) {
          // AGC: V1 contract doesn't have trustedBuff1155Contracts
          isV2Contract = false
          console.log(`  [OK] Detected legacy P2PBattleManager (V1)`)
        }
        
        if (isV2Contract) {
          // AGC: Use V2's payBattleStakeWithNFTs for both buffs and potions
          const buffIdsForPayment = hasNFTs ? selectedBuffs : []
          const potionIdsForPayment = hasPotions ? [selectedPotion] : []
          const buff1155ForPayment = hasNFTs ? buff1155Address : '0x0000000000000000000000000000000000000000'
          const potion1155ForPayment = hasPotions ? classicPotion1155Address : '0x0000000000000000000000000000000000000000'
          
          console.log(`   Paying 50 OGB + ${buffIdsForPayment.length} Buff NFT(s) + ${potionIdsForPayment.length} Potion NFT(s)...`)
          tx = await p2pManager.payBattleStakeWithNFTs(
            battleId,
            ogbTokenAddress,
            buff1155ForPayment,
            potion1155ForPayment,
            buffIdsForPayment,
            potionIdsForPayment
          )
          console.log(`[OK] NFTs payment transaction sent! TX: ${tx.hash}`)
        } else {
          // AGC: V1 contract - only supports buffs OR potions (not both)
          if (hasPotions && hasNFTs) {
            throw new Error('Legacy P2P Manager only supports buffs OR potions, not both. Please select either buffs or potions, but not both.')
          }
          
          // AGC: Use V1 function signature (no ogbTokenAddress, no potion support in same call)
          if (hasPotions) {
            // AGC: V1 with potions only - use potion contract as buff1155Address
            console.log(`   Paying 50 OGB + 1 Potion NFT (using legacy contract)...`)
            const v1Abi = ['function payBattleStakeWithNFTs(bytes32 battleId, address buff1155Address, uint256[] calldata nftIds) external']
            const v1Contract = new ethers.Contract(p2pManagerAddress, v1Abi, signer)
            tx = await v1Contract.payBattleStakeWithNFTs(
              battleId,
              classicPotion1155Address,
              [selectedPotion]
            )
            console.log(`[OK] Potion payment transaction sent! TX: ${tx.hash}`)
          } else if (hasNFTs) {
            // AGC: V1 with buffs only - use old function signature
            console.log(`   Paying 50 OGB + ${selectedBuffs.length} Buff NFT(s) (using legacy contract)...`)
            const v1Abi = ['function payBattleStakeWithNFTs(bytes32 battleId, address buff1155Address, uint256[] calldata nftIds) external']
            const v1Contract = new ethers.Contract(p2pManagerAddress, v1Abi, signer)
            tx = await v1Contract.payBattleStakeWithNFTs(
              battleId,
              buff1155Address,
              selectedBuffs
            )
            console.log(`[OK] Buffs payment transaction sent! TX: ${tx.hash}`)
          }
        }
      } else {
        console.log(' Paying without NFTs (0 buffs, 0 potions)')
        // AGC: Try V2 signature first, fallback to V1 if it fails
        try {
          tx = await p2pManager.payBattleStake(battleId, ogbTokenAddress)
        } catch (error: any) {
          // AGC: V1 contract doesn't need ogbTokenAddress
          const v1Abi = ['function payBattleStake(bytes32 battleId) external']
          const v1Contract = new ethers.Contract(p2pManagerAddress, v1Abi, signer)
          tx = await v1Contract.payBattleStake(battleId)
        }
      }

      console.log(`[WAIT] Payment transaction submitted: ${tx.hash}`)
      console.log('[WAIT] Waiting for transaction confirmation...')

      // AGC: Wait for transaction with timeout (Base App compatibility)
      try {
        await Promise.race([
          tx.wait(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
          )
        ])
        console.log('[OK] Payment transaction confirmed!')
      } catch (waitError: any) {
        if (waitError.message?.includes('timeout')) {
          console.warn(`  ⚠️  Payment TX timeout (TX sent: ${tx.hash}) - continuing...`)
        } else {
          throw waitError
        }
      }

      

      console.log('[OK] Payment confirmed!')

      setHasPaid(true)
      
      // AGC: P2P RECONNECTION - Update sessionStorage with payment status for TopNav indicator
      try {
        const savedMatch = sessionStorage.getItem('p2pPendingMatch')
        if (savedMatch) {
          const matchData = JSON.parse(savedMatch)
          matchData.hasPaid = true
          sessionStorage.setItem('p2pPendingMatch', JSON.stringify(matchData))
          console.log('🔄 [P2PBattle] Updated sessionStorage with payment status')
        }
      } catch (error) {
        console.warn('🔄 [P2PBattle] Failed to update payment status in sessionStorage:', error)
      }

      // AGC: Notify backend

      confirmPayment(battleId, playerAddress, tx.hash)



      showStatusMessage('success', 'Payment confirmed! Waiting for opponent to join…')



    } catch (error: any) {

      console.error('[ERROR] Payment failed:', error)

      console.error('Error details:', {

        message: error.message,

        code: error.code,

        data: error.data,

        reason: error.reason

      })

      

      let errorMessage = error.message || 'Unknown error'

      

      if (error.code === 4001) {

        errorMessage = 'Transaction rejected by user'

      } else if (error.code === -32603) {

        errorMessage = 'Internal JSON-RPC error. Check if you have enough ETH for gas.'

      } else if (error.reason) {

        errorMessage = `Contract error: ${error.reason}`

      }

      

      showStatusMessage('error', `Payment failed: ${errorMessage}`)

      

      // AGC: Don't start battle if payment failed

      setMatchData(null)

      setHasPaid(false)

    } finally {

      setIsPaying(false)

    }

  }



  // AGC: Handle refund for expired battle

  const handleRefund = async () => {

    if (!battleId || !canRefund) return



    setIsRefunding(true)

    

    try {

      const p2pManagerAddress = import.meta.env.VITE_P2P_MANAGER_ADDRESS

      

      if (!p2pManagerAddress) {

        throw new Error('P2P Manager address not configured')

      }



      // AGC: Connect to wallet

      const signer = await resolveSigner()



      // AGC: Call expireBattle function (with buff1155Address to refund NFTs)

      console.log(' Requesting refund for expired battle:', battleId)

      console.log('[WAIT] WAITING FOR WALLET... Check extension icon if popup doesn\'t appear!')

      

      const buff1155Address = import.meta.env.VITE_BUFF1155_ADDRESS
      const ogbTokenAddress = import.meta.env.VITE_OGB_TOKEN_ADDRESS
      const potion1155Address = import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS

      

      if (!buff1155Address) {

        throw new Error('Buff1155 address not configured')

      }
      if (!ogbTokenAddress) {

        throw new Error('OGB Token address not configured')

      }
      
      // AGC: Detect contract version (V1 or V2)
      const provider = signer.provider || new ethers.BrowserProvider(window.ethereum)
      let isV2Contract = false
      try {
        // AGC: Try to call V2 function - if it exists, we have V2
        const checkAbi = ['function trustedBuff1155Contracts(address) public view returns (bool)']
        const checkContract = new ethers.Contract(p2pManagerAddress, checkAbi, provider)
        await checkContract.trustedBuff1155Contracts(buff1155Address)
        isV2Contract = true
        console.log('[OK] Detected P2PBattleManagerV2 for refund')
      } catch (error) {
        // AGC: V1 contract doesn't have trustedBuff1155Contracts
        isV2Contract = false
        console.log('[OK] Detected legacy P2PBattleManager (V1) for refund')
      }

      

      const p2pManagerAbi = isV2Contract
        ? [
            // AGC: V2 signature
            'function expireBattle(bytes32 battleId, address ogbTokenAddress, address buff1155Address, address potion1155Address) external'
          ]
        : [
            // AGC: V1 signature
            'function expireBattle(bytes32 battleId, address buff1155Address) external'
          ]



      const p2pManager = new ethers.Contract(p2pManagerAddress, p2pManagerAbi, signer)



      let tx
      if (isV2Contract) {
        // AGC: V2 requires ogbTokenAddress and potion1155Address
        tx = await p2pManager.expireBattle(
          battleId,
          ogbTokenAddress,
          buff1155Address,
          potion1155Address || '0x0000000000000000000000000000000000000000'
        )
      } else {
        // AGC: V1 uses old signature
        tx = await p2pManager.expireBattle(battleId, buff1155Address)
      }

      

      console.log('[WAIT] Waiting for transaction...', tx.hash)

      try {
        await Promise.race([
          tx.wait(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
          )
        ])
        console.log('[OK] Refund confirmed!')
      } catch (waitError: any) {
        if (waitError.message?.includes('timeout')) {
          console.warn(`  ⚠️  Refund TX timeout (TX sent: ${tx.hash}) - continuing...`)
        } else {
          throw waitError
        }
      }

      showStatusMessage('success', 'Refund successful! Your 50 OGB + NFTs have been returned.')

      

      // AGC: Reset state

      setMatchData(null)

      setHasPaid(false)

      setBattleId('')

      setBattleCreatedAt(0)

      setCanRefund(false)



    } catch (error: any) {

      console.error('[ERROR] Refund failed:', error)

      showStatusMessage('error', `Refund failed: ${error.message || 'Unknown error'}`)

    } finally {

      setIsRefunding(false)

    }

  }



  // AGC: If battle started, show interactive battle component

  if (battleStarted && battleId && opponentData) {

    return (

      <>

        <InteractiveP2PBattle

          battleId={battleId}

          playerAddress={playerAddress}

          opponentAddress={opponentData.address}

          opponentLevel={opponentData.level}

          socket={socket}

          selectedBuffs={selectedBuffs}
          selectedPotion={selectedPotion}

          onBattleEnd={async (won, turns, isDraw = false, resolutionTxHash = null) => { // AGC: Added isDraw and resolutionTxHash parameters

            // AGC: Show auto-closing modal immediately

            setBattleResult({ won, isDraw })

            setShowResultModal(true)

            

            // AGC: Calculate XP for P2P battle

            const xpOutcome: 'WIN' | 'DRAW' | 'LOSE' = isDraw ? 'DRAW' : (won ? 'WIN' : 'LOSE') // AGC: Support draws

            const outcome: 'WIN' | 'LOSS' | 'DRAW' = xpOutcome === 'LOSE' ? 'LOSS' : xpOutcome

            const xpGain = calculateBattleXP(xpOutcome, opponentData.level, 'PVP')

            const currentLevel = playerStats?.level || playerLevel // AGC: Use playerStats level or fallback

            

            console.log(` P2P Battle End:`)

            console.log(`  Outcome: ${outcome}`)

            console.log(`  Turns: ${turns}`) // AGC: Log actual turn count

            console.log(`  Your Level: ${currentLevel}`)

            console.log(`  Opponent Level: ${opponentData.level}`)

            console.log(`  XP Gain: ${xpGain} XP (will be awarded by backend automatically)`)

            console.log(`  Resolution TX: ${resolutionTxHash || 'Not available'}`)

            

            // AGC: Backend already records battle stats and awards XP in endBattle()

            // AGC: No need to call /api/xp/award manually - it's automatic!

            console.log(`[OK] Backend will record battle automatically (no manual API call needed)`)

            

            // AGC: Record P2P battle to history
            addBattleRecord({
              outcome,
              turns, // AGC: Now using actual turn count from backend
              isKnockout: won && turns <= 3, // AGC: KO only if won in 3 or fewer turns (matches PvE logic)
              opponentLevel: opponentData.level,
              playerLevel: currentLevel,
              experienceGained: xpGain,
              tokensWon: isDraw ? 25 : (won ? 75 : 0), // AGC: Draw = 25 OGB refund
              tokensLost: isDraw ? 25 : (won ? 0 : 50), // AGC: Draw = 25 OGB lost (burned NFT)
              opponentAddress: opponentData.address, // AGC: P2P opponent address
              battleType: 'PVP',
              battleId: battleId, // AGC: Link to backend battle log API
              ...(resolutionTxHash && { resolutionTxHash }), // AGC: Conditionally include resolutionTxHash if present
            } as Omit<BattleRecord, 'id' | 'timestamp'>)

          }}

          onBackToMenu={() => {
            // AGC: Navigate back to home
            if (onNavigateToPage) onNavigateToPage('home')
          }}

        />

        

        {/* AGC: Auto-closing battle result modal */}

        <BattleResultModal

          isOpen={showResultModal}

          won={battleResult.won}

          isDraw={battleResult.isDraw}

          onClose={() => {

            setShowResultModal(false)

            setBattleStarted(false)

            setMatchData(null)

            setHasPaid(false)

            setBattleId('')

          }}

          autoCloseDelay={5000} // AGC: 5 seconds

        />

      </>

    )

  }



  // AGC: Top padding style matching Profile.tsx - 72px to clear TopNav (60px) + spacing
  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  // AGC: Calculate XP progress for display (matching Profile.tsx)
  const xpCurrent = playerStats?.experience ?? 0
  const xpToNext = playerStats?.experienceToNext ?? 0
  const xpTarget = xpCurrent + Math.max(0, xpToNext)
  const xpProgress = xpTarget > 0 ? Math.min(1, xpCurrent / xpTarget) : 0
  const attack = playerStats?.attack ?? 0
  const defense = playerStats?.defense ?? 0
  const walletShort = `${playerAddress.slice(0, 4)}…${playerAddress.slice(-4)}`

  return (
    <main
      className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
      style={sharedTopPaddingStyle}
    >
      {/* AGC: Hero Header (No Card) - Following Profile.tsx pattern exactly */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">PVP BATTLE</p>
            <h1 className="text-2xl font-bold leading-tight text-white">Lv {playerLevel}</h1>
            <p className="text-[11px] text-slate-400">{walletShort} • ATK {attack} • DEF {defense}</p>
          </div>
          <a 
            href={`https://sepolia.basescan.org/address/${playerAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
          >
            BaseScan
          </a>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
            <span>XP Progress</span>
            <span>{xpCurrent.toLocaleString()} / {xpTarget.toLocaleString()}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden shadow-inner shadow-black/30">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 transition-all duration-500"
              style={{ width: `${xpProgress * 100}%` }}
      />
          </div>
        </div>
      </section>

      

      {statusBanner && (
        <div
          className={`rounded-xl border px-3 py-2 text-[11px] font-semibold leading-tight ${
            statusBanner.type === 'error'
              ? 'border-rose-500/40 bg-rose-500/10 text-rose-100'
              : 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100'
          }`}
        >
          {statusBanner.message}
        </div>
      )}



        {/* AGC: Matchmaking Settings (before searching) - Using reusable DifficultySelector */}

        {!isSearching && !matchData && (
          <>
            <DifficultySelector
              selectedDifficulty={matchSettings}
              onDifficultySelect={handleMatchSettingSelection}
              disabled={!isConnected || !hasRequiredOgbBalance}
              variant="pvp"
              battleCosts={{
                entryFee: '50 OGB',
                buffNFTsBurned: selectedBuffs.length || 0,
                winnerReceives: '75 OGB (50 + 25 from loser)',
                drawRefund: '25 OGB each (50 OGB burned, NFTs burned)',
                xpAwarded: 'on-chain',
              }}
            />

            {/* AGC: Action Buttons - Find Match */}
            <div className="mt-2">
              <button
                ref={findMatchButtonRef}
                onClick={() => {
                  const currentLevel = playerStats?.level || playerLevel
                  findMatch(playerAddress, currentLevel, matchSettings)
                }}
                disabled={!isConnected || !hasRequiredOgbBalance}
                className={`w-full rounded-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-lg disabled:cursor-not-allowed text-sm uppercase tracking-wide ${
                  highlightFindMatchButton ? 'ring-2 ring-amber-400/70 ring-offset-2 ring-offset-slate-950' : ''
                }`}
              >
                {hasRequiredOgbBalance ? 'Find Match' : '⚠️ Insufficient OGB Balance'}
              </button>
            </div>
            
            {/* AGC: Matchmaking Lobby - Click to join waiting players */}
            <div className="mt-6">
              <MatchmakingLobby
                title="Players Waiting"
                pollIntervalMs={5000}
                collapsedItemCount={3}
                onJoinMatch={handleJoinSpecificMatch}
              />
            </div>
          </>
        )}



        {/* AGC: Searching for Match - Following improve_design.md (no card, border divider) */}

        {isSearching && !matchData && (

          <section className="pb-2 border-b border-white/10">

            <div className="text-center">

              <div className="text-6xl mb-4 animate-pulse"></div>

              <h2 className="text-xl font-bold text-white mb-2">Searching for Opponent...</h2>

              <p className="text-gray-300 mb-4 text-sm">{queueStatus}</p>

              <div className="text-purple-300 text-[11px] mb-4">

                Settings: {matchSettings.toUpperCase()} (±{matchSettings === 'normal' ? 5 : matchSettings === 'expert' ? 10 : 15} levels)

              </div>

              <button

                onClick={() => cancelSearch(playerAddress)}

                className="bg-red-600 hover:bg-red-500 text-white font-semibold py-2 px-6 rounded-lg text-sm"

              >

                Cancel Search

              </button>

            </div>

          </section>

        )}



        {/* AGC: Match Found! */}

        {matchData && !hasPaid && (

          <>
            {/* AGC: Hero Matchup Section - Following improve_design.md (no card, border divider) */}
            <section className="pb-2 border-b border-white/10">

              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase text-emerald-400 tracking-[0.2em]">MATCH FOUND</p>
                <span className="text-2xl">⚔️</span>
              </div>
              
              {/* AGC: Compact matchup - 2 columns, icon-first */}
              <div className="grid grid-cols-2 gap-1.5 mb-2">
                <div className="p-2 border border-purple-500/30">
                  <div className="text-[9px] uppercase text-purple-300 tracking-wide mb-0.5">YOU</div>
                  <div className="text-sm font-bold text-white truncate">{playerAddress.slice(0, 6)}...{playerAddress.slice(-4)}</div>
                  <div className="text-[10px] text-slate-400">Lv {playerStats?.level || playerLevel}</div>
                </div>
                <div className="p-2 border border-blue-500/30">
                  <div className="text-[9px] uppercase text-blue-300 tracking-wide mb-0.5">OPPONENT</div>
                  <div className="text-sm font-bold text-white truncate">{matchData.opponent.address.slice(0, 6)}...{matchData.opponent.address.slice(-4)}</div>
                  <div className="text-[10px] text-slate-400">Lv {matchData.opponent.level}</div>
                </div>
              </div>



              {/* AGC: Compact stakes info - icon + minimal text */}
              <div className="border border-yellow-500/40 px-2 py-1.5">
                <div className="flex items-center gap-1.5 text-[10px] text-yellow-300">
                  <span>💰</span>
                  <span>50 OGB entry • Winner: 75 OGB • 25 OGB burned</span>
                </div>
              </div>
            </section>



            {/* AGC: NFT Buff Selector - Using compact variant */}
            {/* AGC: SAFETY - Show BuffSelector for normal PvP, hide for tournament matches */}
            {/* AGC: Default to showing BuffSelector unless explicitly confirmed as tournament */}
            {!isTournamentMatch || matchData?.tournamentMatch === false ? (
              <>
                <BuffSelector
                  playerAddress={playerAddress}
                  onBuffsSelected={handleBuffSelection}
                  maxBuffs={2}
                  disabled={isPaying || hasPaid}
                  onNavigateToMarketplace={onNavigateToMarketplace}
                  variant="compact"
                  allowBothBuffsAndPotions={true}  // AGC: P2P Manager V2 supports both buffs and potions (like PvE)
                />
                {/* AGC: Note - Potions are now selected via BuffSelector POTIONS tab */}
                {/* AGC: BuffSelector handles both buffs and potions in a unified interface */}
              </>
            ) : isTournamentMatch && matchData?.tournamentMatch === true ? (
              // AGC: SAFETY - Show selected buffs for confirmed tournament match (read-only)
              <section className="space-y-1.5 pb-2 border-b border-white/10">
                <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.25em] text-slate-500">
                  <span>🏆 TOURNAMENT BUFFS (PRE-SELECTED)</span>
                  <span className="text-slate-400">
                    {selectedBuffs.length}/2
                  </span>
                </div>
                {selectedBuffs.length > 0 ? (
                  <div className="space-y-1.5">
                    <p className="text-[9px] text-slate-300">
                      Pre-selected for this match:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedBuffs.map((buffId) => {
                        const buffNames: { [key: number]: { emoji: string; name: string } } = {
                          0: { emoji: '🛡️', name: 'Shield' },
                          1: { emoji: '⚔️', name: 'Pierce' },
                          2: { emoji: '⚡', name: 'Haste' },
                          3: { emoji: '❄️', name: 'Frost' }
                        }
                        const buff = buffNames[buffId] || { emoji: '❓', name: `Buff ${buffId}` }
                        return (
                          <div
                            key={buffId}
                            className="flex items-center gap-1 rounded-full bg-purple-600/20 border border-purple-400/40 px-2 py-1 text-[9px] text-purple-300"
                          >
                            <span>{buff.emoji}</span>
                            <span>{buff.name}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-[9px] text-slate-400 text-center py-2">
                    No buffs selected for this match
                  </p>
                )}
              </section>
            ) : null}

            {/* AGC: Primary Action Button - Game-style, always visible */}
            <button
              ref={payButtonRef}
              onClick={handlePayBattle}
              disabled={isPaying}
              className={`w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-3 px-6 rounded-lg transition-all shadow-lg disabled:cursor-not-allowed text-sm uppercase tracking-wide ${
                highlightPayButton ? 'ring-4 ring-amber-400 ring-offset-2 ring-offset-slate-900 animate-pulse' : ''
              }`}
            >
              {(() => {
                const totalNFTs = selectedBuffs.length + (selectedPotion !== null ? 1 : 0)
                if (isPaying) {
                  return `⏳ Processing (${totalNFTs > 0 ? '3+ steps' : '2 steps'})...`
                }
                if (totalNFTs === 0) {
                  return `💰 Pay 50 OGB`
                }
                return `💰 Pay 50 OGB + ${totalNFTs} NFT${totalNFTs > 1 ? 's' : ''}`
              })()}
            </button>
          </>
        )}



        {/* AGC: Waiting for Opponent Payment - Following improve_design.md (no card, border divider) */}

        {matchData && hasPaid && (

          <section className="pb-2 border-b border-white/10">

              <div className="text-center">
                <div className="text-6xl mb-4 animate-pulse" role="img" aria-hidden>
                  ⏳
                </div>

              <h2 className="text-xl font-bold text-white mb-3">Waiting for Opponent...</h2>

              <p className="text-gray-300 mb-4 text-sm">

                You've paid your 50 OGB stake!<br/>

                Waiting for opponent to pay their stake...

              </p>

              <div className="border border-green-500 p-3 mb-3">
                <div className="text-green-300 text-sm">✅ Your payment confirmed</div>

                <div className="text-gray-400 text-[11px]">Battle will start when both players have paid</div>

              </div>

              

              {/* AGC: Refund Timer and Button */}

              <div className="border border-orange-500 p-3">
                <div className="text-orange-300 font-semibold mb-1 text-sm">⏱️ Auto-Refund Timer</div>

                <div className="text-white text-[11px] mb-2">

                  {canRefund ? (
                    <>✅ Battle expired! You can now claim your refund.</>
                  ) : (
                    <>⏳ If opponent doesn't pay within 3 minutes, you can refund your 50 OGB.<br />
                    <span className="text-orange-400">
                      {Math.floor(timeRemaining / 60)}:{String(Math.floor(timeRemaining % 60)).padStart(2, '0')} remaining
                    </span>
                    </>
                  )}

                </div>

                <button

                  onClick={handleRefund}

                  disabled={!canRefund || isRefunding}

                  className={`w-full font-semibold py-2.5 px-6 rounded-lg transition-all text-sm ${

                    canRefund 

                      ? 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white' 

                      : 'bg-gray-700 text-gray-400 cursor-not-allowed'

                  }`}

                >

                  {isRefunding ? '⏳ Processing Refund...' : canRefund ? 'Claim 50 OGB Refund' : 'Refund Available After 3 Minutes'}

                </button>

              </div>

            </div>

          </section>

        )}

      {/* AGC: Modal components */}
      {Modals}
    </main>
  )

}


